import { ref, watch, onUnmounted } from 'vue'
import { api } from '~/src/services/axios'
import { TOUR_BUILDER_STATE_KEY, TOUR_BUILDER_ATIVO_KEY } from '../utils/storage_keys.js'
import { sugerirPattern } from '../utils/rota_matcher.js'
import './useTourBuilder.scss'

const MARGEM_RECORTE = 10
const MARGEM_ETIQUETA = 8
const SELETOR_PAINEL = '[data-tour-builder-panel]'
const TECLAS_MODIFICADORAS = ['Control', 'Meta']

const IDENTIFICADOR_CSS = /^-?[_a-zA-Z\u00A0-\uFFFF][\w\u00A0-\uFFFF-]*$/

export function useTourBuilder({ hidratarAtivo = true } = {}) {
  const passos = ref([])
  const ativo = ref(false)
  const editando = ref(null)
  const idsPersistidosRemovidos = ref([])
  const persistenciaSuspensa = ref(false)
  const nomeTour = ref('')
  const rotaPattern = ref('')
  const tourExistenteId = ref(null)
  const rotaPatternTravada = ref(false)

  function extrairErro(erro, fallback) {
    const erros = erro?.response?.data?.erros
    if (Array.isArray(erros) && erros.length > 0) return erros.join(', ')
    return fallback
  }

  function caminhoAteAncestral(el, ancestral) {
    const partes = []
    let cur = el
    while (cur && cur !== ancestral) {
      const tag = cur.tagName.toLowerCase()
      const pai = cur.parentElement
      if (!pai) break
      const irmaosMesmoTag = Array.from(pai.children).filter((c) => c.tagName === cur.tagName)
      if (irmaosMesmoTag.length === 1) {
        partes.unshift(tag)
      } else {
        const indice = irmaosMesmoTag.indexOf(cur) + 1
        partes.unshift(`${tag}:nth-of-type(${indice})`)
      }
      cur = pai
    }
    return partes.join(' > ')
  }

  function gerarSeletor(el) {
    if (el.dataset?.tourId) return `[data-tour-id="${el.dataset.tourId}"]`
    if (IDENTIFICADOR_CSS.test(el.id)) return `#${el.id}`
    const tag = el.tagName.toLowerCase()
    const classes = Array.from(el.classList)
      .filter((classe) => IDENTIFICADOR_CSS.test(classe))
      .slice(0, 2)
      .join('.')
    const seletorBase = classes ? `${tag}.${classes}` : tag
    if (document.querySelectorAll(seletorBase).length <= 1) return seletorBase
    const ancestral = el.closest('.modal[id], [id]:not(html):not(body)')
    if (!ancestral || !IDENTIFICADOR_CSS.test(ancestral.id)) return seletorBase

    const ancestralSeletor = ancestral.classList.contains('modal') ? `.modal#${ancestral.id}` : `#${ancestral.id}`
    const seletorAncorado = `${ancestralSeletor} ${seletorBase}`
    if (document.querySelectorAll(seletorAncorado).length === 1) return seletorAncorado

    const caminho = caminhoAteAncestral(el, ancestral)
    return caminho ? `${ancestralSeletor} > ${caminho}` : seletorAncorado
  }

  function rotuloAmigavel(el) {
    const texto = el.innerText?.trim().slice(0, 40)
    if (texto) return texto
    if (el.dataset?.tourId) return el.dataset.tourId
    return el.tagName.toLowerCase()
  }

  function persistirEstado() {
    try {
      const payload = {
        passos: passos.value,
        ativo: ativo.value,
        nomeTour: nomeTour.value,
        rotaPattern: rotaPattern.value,
        tourExistenteId: tourExistenteId.value,
        rotaPatternTravada: rotaPatternTravada.value,
        idsPersistidosRemovidos: idsPersistidosRemovidos.value,
        atualizado_em: Date.now()
      }
      sessionStorage.setItem(TOUR_BUILDER_STATE_KEY, JSON.stringify(payload))
    } catch (e) {
      console.warn('Falha ao persistir estado do builder:', e)
    }
  }

  function lerEstadoSalvo() {
    try {
      const bruto = sessionStorage.getItem(TOUR_BUILDER_STATE_KEY)
      if (!bruto) return null
      return JSON.parse(bruto)
    } catch (e) {
      console.warn('Falha ao ler estado do builder:', e)
      return null
    }
  }

  function limparPersistencia() {
    try {
      sessionStorage.removeItem(TOUR_BUILDER_STATE_KEY)
    } catch (e) {
      console.warn('Falha ao limpar estado do builder:', e)
    }
  }

  let previa = null
  let ultimoAlvo = null
  let alvoRenderizado = null

  function criarNo(tag, classe) {
    const no = document.createElement(tag)
    no.className = classe
    no.hidden = true
    return no
  }

  function criarPrevia() {
    const recorte = criarNo('div', 'tour-builder-previa')
    const etiqueta = criarNo('div', 'tour-builder-previa-etiqueta')
    const rotulo = document.createElement('span')
    rotulo.className = 'tour-builder-previa-etiqueta__rotulo'
    const seletor = document.createElement('code')
    seletor.className = 'tour-builder-previa-etiqueta__seletor'
    const aviso = criarNo('span', 'tour-builder-previa-etiqueta__aviso')
    etiqueta.append(rotulo, seletor, aviso)
    document.body.append(recorte, etiqueta)
    previa = { recorte, etiqueta, rotulo, seletor, aviso }
  }

  function removerPrevia() {
    if (previa) {
      previa.recorte.remove()
      previa.etiqueta.remove()
    }
    previa = null
    ultimoAlvo = null
    alvoRenderizado = null
  }

  function avaliarSeletor(el) {
    try {
      const seletor = gerarSeletor(el)
      const encontrados = document.querySelectorAll(seletor)
      const especifico = seletor !== el.tagName.toLowerCase()
      return {
        seletor: especifico ? seletor : '',
        contagem: encontrados.length,
        especifico,
        ambiguo: !especifico || encontrados.length > 1 || encontrados[0] !== el
      }
    } catch (erro) {
      if (erro.name !== 'SyntaxError') throw erro
      return { seletor: '', contagem: 0, especifico: false, ambiguo: true }
    }
  }

  function esconderPrevia() {
    if (!previa || previa.recorte.hidden) return
    previa.recorte.hidden = true
    previa.etiqueta.hidden = true
    alvoRenderizado = null
  }

  function textoAviso({ especifico, contagem }) {
    if (!especifico) return '⚠ Não há um seletor estável para este elemento — adicione um id ou data-tour-id'
    const unidade = contagem === 1 ? 'elemento' : 'elementos'
    return `⚠ Seletor encontra ${contagem} ${unidade} na tela — o passo pode apontar para outro item`
  }

  function preencherEtiqueta(el) {
    const { seletor, contagem, especifico, ambiguo } = avaliarSeletor(el)
    previa.rotulo.textContent = rotuloAmigavel(el)
    previa.seletor.textContent = seletor
    previa.aviso.textContent = ambiguo ? textoAviso({ especifico, contagem }) : ''
    previa.aviso.hidden = !ambiguo
    previa.recorte.classList.toggle('tour-builder-previa--ambiguo', ambiguo)
    previa.etiqueta.classList.toggle('tour-builder-previa-etiqueta--ambiguo', ambiguo)
  }

  function posicionarPrevia(el) {
    const rect = el.getBoundingClientRect()
    const topoRecorte = rect.top - MARGEM_RECORTE
    const esquerdaRecorte = rect.left - MARGEM_RECORTE
    const alturaRecorte = rect.height + MARGEM_RECORTE * 2
    const { recorte, etiqueta } = previa
    const alturaEtiqueta = etiqueta.offsetHeight
    const larguraEtiqueta = etiqueta.offsetWidth

    recorte.style.top = `${topoRecorte}px`
    recorte.style.left = `${esquerdaRecorte}px`
    recorte.style.width = `${rect.width + MARGEM_RECORTE * 2}px`
    recorte.style.height = `${alturaRecorte}px`

    const topoAcima = topoRecorte - alturaEtiqueta - MARGEM_ETIQUETA
    const topoEtiqueta = topoAcima < 0 ? topoRecorte + alturaRecorte + MARGEM_ETIQUETA : topoAcima
    const esquerdaMaxima = Math.max(0, window.innerWidth - larguraEtiqueta)
    etiqueta.style.top = `${topoEtiqueta}px`
    etiqueta.style.left = `${Math.min(Math.max(esquerdaRecorte, 0), esquerdaMaxima)}px`
  }

  function mostrarPrevia(el) {
    if (!previa) return
    if (!el || el.closest(SELETOR_PAINEL)) {
      esconderPrevia()
      return
    }
    if (el !== alvoRenderizado) {
      preencherEtiqueta(el)
      alvoRenderizado = el
    }
    previa.recorte.hidden = false
    previa.etiqueta.hidden = false
    posicionarPrevia(el)
  }

  function aoMoverMouse(e) {
    ultimoAlvo = e.target
    if (e.ctrlKey || e.metaKey) {
      mostrarPrevia(e.target)
    } else {
      esconderPrevia()
    }
  }

  function aoPressionarTecla(e) {
    if (TECLAS_MODIFICADORAS.includes(e.key)) mostrarPrevia(ultimoAlvo)
  }

  function aoSoltarTecla(e) {
    if (TECLAS_MODIFICADORAS.includes(e.key)) esconderPrevia()
  }

  function aoPerderFoco() {
    esconderPrevia()
  }

  function aoRolar() {
    if (alvoRenderizado) posicionarPrevia(alvoRenderizado)
  }

  function ligarPrevia() {
    criarPrevia()
    document.addEventListener('mousemove', aoMoverMouse, { capture: true, passive: true })
    document.addEventListener('keydown', aoPressionarTecla)
    document.addEventListener('keyup', aoSoltarTecla)
    window.addEventListener('blur', aoPerderFoco)
    document.addEventListener('scroll', aoRolar, { capture: true, passive: true })
  }

  function desligarPrevia() {
    document.removeEventListener('mousemove', aoMoverMouse, { capture: true })
    document.removeEventListener('keydown', aoPressionarTecla)
    document.removeEventListener('keyup', aoSoltarTecla)
    window.removeEventListener('blur', aoPerderFoco)
    document.removeEventListener('scroll', aoRolar, { capture: true })
    removerPrevia()
  }

  function interceptarClique(e) {
    if (e.target.closest(SELETOR_PAINEL)) return
    if (!e.ctrlKey && !e.metaKey) return

    e.preventDefault()
    e.stopPropagation()

    const seletor = gerarSeletor(e.target)
    if (passos.value.find((p) => p.seletor_alvo === seletor)) return

    const novoPasso = {
      id: Date.now(),
      posicao: passos.value.length + 1,
      seletor_alvo: seletor,
      rota_pattern: sugerirPattern(window.location.pathname),
      titulo: '',
      descricao: '',
      posicionamento: 'bottom',
      dispara_clique: false,
      dispara_hover: false,
      rotulo: rotuloAmigavel(e.target),
      seletor_estavel: Boolean(e.target.dataset?.tourId)
    }
    passos.value.push(novoPasso)
    editando.value = novoPasso.id
  }

  function sinalizarBuilderAtivo() {
    try {
      sessionStorage.setItem(TOUR_BUILDER_ATIVO_KEY, '1')
    } catch (e) {
      console.warn('Falha ao sinalizar builder ativo:', e)
    }
    window.dispatchEvent(new CustomEvent('tour-builder:ativado'))
  }

  function limparSinalBuilderAtivo() {
    try {
      sessionStorage.removeItem(TOUR_BUILDER_ATIVO_KEY)
    } catch (e) {
      console.warn('Falha ao limpar sinal de builder ativo:', e)
    }
  }

  function ativar() {
    if (ativo.value) return
    ativo.value = true
    document.addEventListener('click', interceptarClique, true)
    document.body.style.cursor = 'crosshair'
    ligarPrevia()
    sinalizarBuilderAtivo()
  }

  function desativar() {
    if (!ativo.value) {
      limparSinalBuilderAtivo()
      return
    }
    ativo.value = false
    document.removeEventListener('click', interceptarClique, true)
    document.body.style.cursor = ''
    desligarPrevia()
    limparSinalBuilderAtivo()
  }

  function removerPasso(id) {
    const alvo = passos.value.find((p) => p.id === id)
    if (alvo?.idPersistido) {
      idsPersistidosRemovidos.value = [...idsPersistidosRemovidos.value, alvo.idPersistido]
    }
    passos.value = passos.value
      .filter((p) => p.id !== id)
      .map((p, i) => ({ ...p, posicao: i + 1 }))
  }

  function moverPasso(index, direcao) {
    const arr = [...passos.value]
    const destino = index + direcao
    if (destino < 0 || destino >= arr.length) return
    ;[arr[index], arr[destino]] = [arr[destino], arr[index]]
    passos.value = arr.map((p, i) => ({ ...p, posicao: i + 1 }))
  }

  function publicar(nome, rotaPattern) {
    const payload = {
      tour_guiado: {
        nome,
        rota_pattern: rotaPattern,
        tour_passos_attributes: passos.value.map((p) => ({
          posicao: p.posicao,
          seletor_alvo: p.seletor_alvo,
          titulo: p.titulo,
          descricao: p.descricao,
          posicionamento: p.posicionamento,
          rota_pattern: p.rota_pattern,
          dispara_clique: Boolean(p.dispara_clique),
          dispara_hover: Boolean(p.dispara_hover)
        }))
      }
    }

    return api.post('/tours_guiados', payload)
      .then(({ data }) => {
        limparPersistencia()
        return data
      })
      .catch((erro) => {
        throw new Error(extrairErro(erro, 'Erro ao salvar tour'))
      })
  }

  function carregarPassos(passosDoServidor) {
    persistenciaSuspensa.value = true
    idsPersistidosRemovidos.value = []
    if (!Array.isArray(passosDoServidor)) {
      passos.value = []
      return
    }
    passos.value = passosDoServidor.map((p, indice) => ({
      id: p.id ?? Date.now() + indice,
      idPersistido: p.id ?? null,
      posicao: p.posicao ?? indice + 1,
      seletor_alvo: p.seletor_alvo ?? '',
      rota_pattern: p.rota_pattern ?? '',
      titulo: p.titulo ?? '',
      descricao: p.descricao ?? '',
      posicionamento: p.posicionamento ?? 'bottom',
      dispara_clique: Boolean(p.dispara_clique),
      dispara_hover: Boolean(p.dispara_hover),
      rotulo: p.rotulo ?? p.seletor_alvo ?? '',
      seletor_estavel: typeof p.seletor_alvo === 'string' && p.seletor_alvo.includes('data-tour-id')
    }))
    editando.value = null
  }

  function atualizar(id, nome, rotaPattern) {
    const atributosPassos = [
      ...passos.value.map((p) => {
        const atributos = {
          posicao: p.posicao,
          seletor_alvo: p.seletor_alvo,
          titulo: p.titulo,
          descricao: p.descricao,
          posicionamento: p.posicionamento,
          rota_pattern: p.rota_pattern,
          dispara_clique: Boolean(p.dispara_clique),
          dispara_hover: Boolean(p.dispara_hover)
        }
        if (p.idPersistido) atributos.id = p.idPersistido
        return atributos
      }),
      ...idsPersistidosRemovidos.value.map((idRemovido) => ({ id: idRemovido, _destroy: true }))
    ]

    const payload = {
      tour_guiado: {
        nome,
        rota_pattern: rotaPattern,
        tour_passos_attributes: atributosPassos
      }
    }

    return api.patch(`/tours_guiados/${id}`, payload)
      .then(({ data }) => {
        idsPersistidosRemovidos.value = []
        return data
      })
      .catch((erro) => {
        throw new Error(extrairErro(erro, 'Erro ao atualizar tour'))
      })
  }

  async function carregarTourEditavel(rota) {
    try {
      const { data } = await api.get('/tours_guiados/editavel', { params: { rota } })
      if (!data || !Array.isArray(data.passos) || data.passos.length === 0) return null

      idsPersistidosRemovidos.value = []
      passos.value = data.passos.map((p, i) => ({
        id: p.id || Date.now() + i,
        idPersistido: p.id ?? null,
        posicao: p.posicao || i + 1,
        seletor_alvo: p.seletor_alvo,
        rota_pattern: p.rota_pattern,
        titulo: p.titulo,
        descricao: p.descricao,
        posicionamento: p.posicionamento,
        dispara_clique: Boolean(p.dispara_clique),
        dispara_hover: Boolean(p.dispara_hover),
        rotulo: p.titulo || p.seletor_alvo,
        seletor_estavel: typeof p.seletor_alvo === 'string' && p.seletor_alvo.startsWith('[data-tour-id=')
      }))
      editando.value = null
      tourExistenteId.value = data.id
      nomeTour.value = data.nome
      rotaPattern.value = data.rota_pattern
      rotaPatternTravada.value = true
      return { id: data.id, nome: data.nome, rota_pattern: data.rota_pattern }
    } catch (e) {
      console.warn('Falha ao carregar tour editavel:', e)
      return null
    }
  }

  function limpar() {
    passos.value = []
    editando.value = null
    idsPersistidosRemovidos.value = []
    nomeTour.value = ''
    rotaPattern.value = ''
    tourExistenteId.value = null
    rotaPatternTravada.value = false
    limparPersistencia()
  }

  const estadoSalvo = lerEstadoSalvo()
  if (estadoSalvo) {
    nomeTour.value = estadoSalvo.nomeTour ?? nomeTour.value
    rotaPattern.value = estadoSalvo.rotaPattern ?? rotaPattern.value
    tourExistenteId.value = estadoSalvo.tourExistenteId ?? tourExistenteId.value
    rotaPatternTravada.value = estadoSalvo.rotaPatternTravada ?? rotaPatternTravada.value
    if (Array.isArray(estadoSalvo.idsPersistidosRemovidos)) {
      idsPersistidosRemovidos.value = estadoSalvo.idsPersistidosRemovidos
    }
    if (Array.isArray(estadoSalvo.passos) && estadoSalvo.passos.length > 0) {
      passos.value = estadoSalvo.passos
      if (estadoSalvo.ativo && hidratarAtivo) ativar()
    }
  }

  watch(
    [passos, ativo, nomeTour, rotaPattern, tourExistenteId, rotaPatternTravada, idsPersistidosRemovidos],
    () => {
      if (persistenciaSuspensa.value) return
      if (ativo.value || passos.value.length > 0) {
        persistirEstado()
      }
    },
    { deep: true }
  )

  watch(
    () => passos.value[0]?.rota_pattern,
    (novo) => {
      if (rotaPatternTravada.value) return
      if (!novo) return
      rotaPattern.value = novo
    },
    { immediate: true }
  )

  onUnmounted(desativar)

  return {
    passos,
    ativo,
    editando,
    idsPersistidosRemovidos,
    nomeTour,
    rotaPattern,
    tourExistenteId,
    rotaPatternTravada,
    ativar,
    desativar,
    removerPasso,
    moverPasso,
    publicar,
    carregarPassos,
    atualizar,
    carregarTourEditavel,
    limpar
  }
}
